// Saved tabs and the "Restore N tabs" offer: the rules, with no DOM or storage, so
// test/restore.test.mjs can check them directly. app/session-store.js does the reading and writing.

export const MAX_TABS = 20;
export const MAX_URL = 2048;
export const MAX_TITLE = 120;
export const OFFER_TTL = 7 * 24 * 3600 * 1000; // an unanswered offer is forgotten after a week

const okUrl = (u) => typeof u === "string" && u.length <= MAX_URL && /^https?:\/\/[^\s]+$/i.test(u);
// Internal pages are first-party paths only ("/settings", never "//host" or a scheme).
const okPath = (p) => typeof p === "string" && p.length <= 200 && p.charAt(0) === "/" && p.charAt(1) !== "/";
const okView = (v) => typeof v === "string" && /^[a-z0-9-]{1,40}$/.test(v);

function cleanTitle(s) {
	// eslint-disable-next-line no-control-regex
	const t = String(s == null ? "" : s).replace(/[\u0000-\u001f\u007f-\u009f\u200b-\u200f\u202a-\u202e\u2060-\u2069\ufeff]/g, "").replace(/\s+/g, " ").trim();
	return t.slice(0, MAX_TITLE);
}

// One tab as it is saved, or null when it has nothing worth reopening. Tabs opened in incognito
// (t.priv) are never saved, even after incognito is switched off.
export function snapOf(t) {
	if (!t || t.priv) return null;
	const base = { title: cleanTitle(t.title) };
	if (t.zoom && t.zoom !== 100) base.zoom = t.zoom;
	let s = null;
	if (t.restore) s = { kind: t.restore.kind, target: t.restore.target };
	else if (t.view) s = { kind: "view", target: t.view };
	else if (t.internal) s = { kind: "internal", target: t.internal };
	else if (t.url) s = { kind: "url", target: t.url };
	if (!s) return null;
	if (s.kind === "url" ? !okUrl(s.target) : s.kind === "internal" ? !okPath(s.target) : s.kind === "view" ? !okView(s.target) : true) return null;
	return Object.assign(s, base);
}

// The whole session: at most MAX_TABS, and the tab you were on is always one of them.
export function snapTabs(tabs, activeId, now) {
	let list = [];
	let active = 0;
	for (const t of tabs || []) {
		const s = snapOf(t);
		if (!s) continue;
		if (t.id === activeId) active = list.length;
		list.push(s);
	}
	if (!list.length) return null;
	if (list.length > MAX_TABS) {
		const keep = list[active];
		list = list.slice(0, MAX_TABS);
		if (active >= MAX_TABS) { list[MAX_TABS - 1] = keep; active = MAX_TABS - 1; }
	}
	return { tabs: list, active, at: now };
}

// A stored session read back: anything malformed is dropped rather than trusted.
export function parseSnap(raw) {
	let v = raw;
	if (typeof raw === "string") { try { v = JSON.parse(raw); } catch { return null; } }
	if (!v || !Array.isArray(v.tabs)) return null;
	const tabs = v.tabs.slice(0, MAX_TABS).map((t) => snapOf(t && { title: t.title, zoom: t.zoom, restore: { kind: t.kind, target: t.target } })).filter(Boolean);
	if (!tabs.length) return null;
	const active = Math.max(0, Math.min(Number(v.active) | 0, tabs.length - 1));
	return { tabs, active, at: Number(v.at) || 0 };
}

// What to do at launch.
//   saved: the session the last page left behind; offer: an older unanswered offer;
//   auto:  Re:Vault itself reloaded the page (an update or a self-repair), so tabs come back
//          without asking, as before.
// Returns { restore, offer }: a session to rebuild at once, and/or one to offer.
export function decide({ saved, offer, auto, enabled, incognito, now }) {
	if (!enabled) return { restore: null, offer: null, clear: true };
	if (saved && auto && !incognito) return { restore: saved, offer: null };
	const pick = saved || (offer && now - (offer.at || 0) < OFFER_TTL ? offer : null);
	return { restore: null, offer: incognito ? null : pick, keep: pick };
}

// Short names for the offer line: site names and first-party page names, never full addresses.
export function offerNames(snap, max = 3) {
	const names = [];
	for (const t of (snap && snap.tabs) || []) {
		let n = "";
		if (t.kind === "url") { try { n = new URL(t.target).hostname.replace(/^www\./, ""); } catch { n = ""; } }
		else n = cleanTitle(t.title).slice(0, 40);
		if (n && !names.includes(n)) names.push(n);
	}
	const shown = names.slice(0, max);
	return { shown, more: Math.max(0, names.length - shown.length) };
}

export function offerLine(snap) {
	const { shown, more } = offerNames(snap);
	if (!shown.length) return "";
	if (!more) return shown.length === 1 ? shown[0] : shown.slice(0, -1).join(", ") + " and " + shown[shown.length - 1];
	return shown.join(", ") + " and " + more + " more";
}

export const restoreLabel = (n) => "Restore " + n + (n === 1 ? " tab" : " tabs");
